
import { useState, useCallback, useMemo, useEffect } from "react";
import {
  useGetAdminDataQuery, 
  useCreateUserMutation,
  useEditUserMutation,
  useDeleteUserMutation,
  useRestoreUserMutation,
  useResetPasswordMutation,
  useAssignUserToGroupAdminMutation,
  useRemoveManagedUserMutation,
  useAddDeviceMutation,
  useEditDeviceMutation,
  useDeleteDeviceMutation,
  useLazyValidateIMEIQuery, 
  useAddAssetMutation,
  useEditAssetMutation,
  useUpdateAssetStatusMutation,
  usePermanentDeleteAssetMutation,
} from "../../redux/admin/adminApi"; 
import { getCreatableRoles } from "../../utils/rbacHelper";
import { useStatus } from "../../popup/NotificationContext";
import { downloadEntityTemplate, uploadEntityFile } from "../../services/importExportService";
import { getReadableStatusMessage } from "../../utils/statusMessage";
import { verifyRelayAccess } from "../../utils/relayAccess";
import carImg from "../../assets/car.png";
import supercar from "../../assets/supercar.png";
import truckImg from "../../assets/truck.png";
import busImg from "../../assets/supercar.png";
import vanImg from "../../assets/van.png";
import bikeImg from "../../assets/jeep.png";
import tractorImg from "../../assets/tracktor.png";
import cycle from "../../assets/cycle.png";
import police from "../../assets/police.png";
import exporter from "../../assets/exporter.png";
import taxi from "../../assets/taxi.png";
import taxi2 from "../../assets/taxi1.png";
import jcb from "../../assets/JCB.png";
import plane from "../../assets/plane.png";
import jeep from "../../assets/jeep.png";
import cargosheep from "../../assets/Cargoship.png";
import mountainjeep from "../../assets/mountainjeep.png";


export default function useAdminHandlers({
  currentUser, handleLogout, resetUserId, newPassword: modalNewPassword, confirmPassword,
  setNewPassword: setModalNewPassword, setConfirmPassword, setShowResetPasswordModal,
  editingAsset, setEditingAsset, setShowEditAssetModal, newAssetData, setNewAssetData,
  setShowAddAssetModal, assignDeviceId, assignToUserId, setAssignDeviceId, setAssignToUserId,
  assignUserToGroupAdminUserId, assignUserToGroupAdminTargetId, setAssignUserToGroupAdminUserId, setAssignUserToGroupAdminTargetId,
  editingUser, setEditingUser, editingDevice, setEditingDevice, setShowEditDeviceModal,
  setShowAddDeviceModal, selectedDeviceForGeofence, setSelectedDeviceForGeofence, unsavedGeofence,
  geofenceName, setShowGeofenceModal, setUnsavedGeofence, setGeofenceName, setRouteStart, setRouteEnd, setRouteDetails,
  setLocationError, setShowLocationError, fetchAdminData, devices 
}) {
  
  const { showStatus } = useStatus(); 

  // --- RTK Hooks ---
  const [createUser] = useCreateUserMutation();
  const [editUser] = useEditUserMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [restoreUser] = useRestoreUserMutation();
  const [resetPassword] = useResetPasswordMutation();
  const [assignUserToGroupAdmin] = useAssignUserToGroupAdminMutation();
  const [removeManagedUser] = useRemoveManagedUserMutation();
  const [addDevice] = useAddDeviceMutation();
  const [editDevice] = useEditDeviceMutation();
  const [deleteDevice] = useDeleteDeviceMutation();
  const [addAsset] = useAddAssetMutation();
  const [editAsset] = useEditAssetMutation();
  const [updateAssetStatus] = useUpdateAssetStatusMutation();
  const [permanentDeleteAsset] = usePermanentDeleteAssetMutation();
  const [triggerIMEIValidation] = useLazyValidateIMEIQuery();

  useGetAdminDataQuery(undefined, { skip: !currentUser });

  // --- Local States ---
  const [newDeviceModel, setNewDeviceModel] = useState("");
  const [newDeviceName, setNewDeviceName] = useState("");
  const [newDeviceIMEI, setNewDeviceIMEI] = useState("");
  const [newDeviceMSDN, setNewDeviceMSDN] = useState("");
  const [newDeviceIMSI, setNewDeviceIMSI] = useState("");
  const [newDeviceAssignedToUser, setNewDeviceAssignedToUser] = useState("");
  const [newDeviceType, setNewDeviceType] = useState("hardwire"); // Default set
  const [isIMEIValid, setIsIMEIValid] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newMobile, setNewMobile] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newUserRole, setNewUserRole] = useState("");
  const [bulkFile, setBulkFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deviceForCommand, setDeviceForCommand] = useState(null);
  const [showCommandModal, setShowCommandModal] = useState(false);
  const [commandModalOptions, setCommandModalOptions] = useState({
    uiMode: "simple",
    commandScope: "full",
  });
  const [newDeviceVehicleType, setNewDeviceVehicleType] = useState("car");
  const isValidMongoId = (id) => typeof id === 'string' && /^[0-9a-fA-F]{24}$/.test(id);

  useEffect(() => {
    if (currentUser?.role) {
      const roles = getCreatableRoles(currentUser.role);
      setNewUserRole(roles.length ? roles[0] : "");
    }
  }, [currentUser?.role]);

  const formatTimestamp12h = useCallback((timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString('en-GB', { hour12: true }).toUpperCase();
  }, []);

// Vehicle icon mapping object
const vehicleTypeIcons = useMemo(() => ({
  car: carImg,
  jeep: jeep,
  cargoship: cargosheep,
  truck: truckImg,
  mountainjeep: mountainjeep,
  bus: busImg,
  van: vanImg,
  bike: bikeImg,
  cycle: cycle,
  police: police,
  exporter: exporter,
  tractor: tractorImg,
  taxi: taxi,
  taxi1: taxi2,
  plane: plane,
  jcb: jcb,
  supercar: supercar,
  default: carImg
}), []);

// Function to get correct icon
const getVehicleIcon = useCallback((vehicleType) => {
  if (!vehicleType) return vehicleTypeIcons.default;
  const normalized = vehicleType.toLowerCase().trim();
  return vehicleTypeIcons[normalized] || vehicleTypeIcons.default;
}, [vehicleTypeIcons]);


  const handleOpenCommandModal = useCallback(async (device, options = {}) => {
    try {
      const access = await verifyRelayAccess(options);
      if (!access?.ok) {
        showStatus("warning", access?.message || "Relay access denied.");
        return;
      }
    } catch (error) {
      showStatus("error", error?.response?.data?.msg || error?.message || "Failed to verify relay access.");
      return;
    }

    setDeviceForCommand(device);
    setCommandModalOptions({
      uiMode: options.uiMode || "simple",
      commandScope: options.commandScope || "full",
      initialCommandType: options.initialCommandType || "",
    });
    setShowCommandModal(true);
  }, [showStatus]);
  const handleCloseCommandModal = useCallback(() => {
    setShowCommandModal(false);
    setDeviceForCommand(null);
    setCommandModalOptions({
      uiMode: "simple",
      commandScope: "full",
      initialCommandType: "",
    });
  }, []);

  // ---------------------- HANDLERS ----------------------

  const handleRestoreUser = useCallback(async (userId) => {
    try { await restoreUser(userId).unwrap(); showStatus('success', "User restored successfully."); } 
    catch (err) { showStatus('error', getReadableStatusMessage(err, "error")); }
  }, [restoreUser, showStatus]);

  const handleResetPassword = useCallback(async (e) => {
    e.preventDefault();
    if (modalNewPassword !== confirmPassword) { showStatus('warning', "New password and confirm password do not match."); return; }
    try { await resetPassword({ userId: resetUserId, password: modalNewPassword }).unwrap(); showStatus('success', "Password updated successfully."); setShowResetPasswordModal(false); } 
   catch (err) {
  if (err?.status === 401) {
    handleLogout();
  } else {
    showStatus(
      'error',
      err?.data?.msg || err?.data?.message || "You are not allowed to perform this action."
    );
  }
}

  }, [resetUserId, modalNewPassword, confirmPassword, resetPassword, setShowResetPasswordModal, showStatus]);

  const handleCreateUser = useCallback(async (e, customData) => {
    if (e) e.preventDefault();
    const userData = customData ? {
        username: customData.username,
        email: customData.email,
        mobileNo: customData.mobileNo,
        password: customData.password,
        role: customData.role,
        uniqueId: customData.uniqueId,
        permissions: customData.permissions,
        devicePermissions: customData.devicePermissions,
        userPermissions: customData.userPermissions,
        deviceLimit: customData.deviceLimit,
        userLimit: customData.userLimit,
        speedUnit: customData.speedUnit,
        distanceUnit: customData.distanceUnit,
        timezone: customData.timezone,
        preferences: customData.preferences,
        attributes: customData.attributes,
    } : { 
      username: newUsername, 
      email: newEmail, 
      mobileNo: newMobile, 
      password: newPassword, 
      role: newUserRole 
    };
    
    try { 
      const response = await createUser(userData).unwrap(); 
      showStatus('success', response.msg || "User account created successfully.");
      if(!customData) { 
        setNewUsername(""); setNewEmail(""); setNewMobile(""); setNewPassword(""); 
      }
      if (fetchAdminData) fetchAdminData();
      return true;
    } 
    catch (err) { 
      showStatus('error', getReadableStatusMessage(err, "error"));
      return false;
    }
  }, [newUsername, newEmail, newMobile, newPassword, newUserRole, createUser, showStatus, fetchAdminData]);

  const handleEditUser = useCallback(async (e) => {
    e.preventDefault();
    try { await editUser({ userId: editingUser._id, ...editingUser }).unwrap(); setEditingUser(null); showStatus('success', "User updated successfully."); } 
    catch (err) { showStatus('error', getReadableStatusMessage(err, "error")); }
  }, [editingUser, editUser, setEditingUser, showStatus]);

  const handleDeleteUser = useCallback(async (userId) => {
    if (!window.confirm("Delete this user permanently?")) return;
    try { await deleteUser(userId).unwrap(); showStatus('success', "User deleted successfully."); } 
    catch (err) { showStatus('error', getReadableStatusMessage(err, "error")); }
  }, [deleteUser, showStatus]);

const handleIMEIValidation = useCallback(async (e) => {
  e.preventDefault();
  
  if (!newDeviceIMEI || newDeviceIMEI.length !== 15) {
    showStatus('warning', "IMEI must be exactly 15 digits");
    return;
  }
  
  try {
    const response = await triggerIMEIValidation(newDeviceIMEI).unwrap();
    
    console.log("🔍 IMEI Validation Full Response:", response);
    
    if (response.success && response.asset) {
      const asset = response.asset;
      
      // Auto-fill device details
      setNewDeviceName(asset.name || ""); 
      setNewDeviceModel(asset.model || ""); 
      setNewDeviceType(asset.type || "hardwire");
      
      // 🎯 FIX: Auto-fill SIM details from asset.simCard
      const simCard = asset.simCard || {};
      console.log("📱 SIM Card Data:", simCard);
      
      setNewDeviceMSDN(simCard.MSDN || "");
      setNewDeviceIMSI(simCard.IMSI || "");
      
      setIsIMEIValid(true);
      
      let successMsg = `✓ IMEI Validated! Model: ${asset.model || 'Unknown'}`;
      if (simCard.MSDN) successMsg += ` | SIM: ${simCard.MSDN}`;
      if (simCard.IMSI) successMsg += ` | IMSI: ${simCard.IMSI}`;
      
      showStatus('success', successMsg);
    } else {
      showStatus('error', response.message || "No asset was found for this IMEI.");
    }
  } catch (err) {
    console.error("IMEI Validation Error:", err);
    showStatus('error', getReadableStatusMessage(err, "error"));
  }
}, [newDeviceIMEI, triggerIMEIValidation, showStatus]);

const handleAddDevice = useCallback(async (e) => {
  if (e) e.preventDefault();
  
  // 🎯 Field-level validation with specific messages
  const errors = [];
  
  if (!newDeviceIMEI || newDeviceIMEI.length !== 15) {
    errors.push("IMEI must be 15 digits");
  }
  if (!newDeviceName || newDeviceName.trim().length < 2) {
    errors.push("Vehicle name is required (min 2 characters)");
  }
  if (!newDeviceModel) {
    errors.push("Device model is required");
  }
  
  if (errors.length > 0) {
    showStatus('warning', `Please fix these fields: ${errors.join(", ")}.`);
    return;
  }

  try {
    const validUser = isValidMongoId(newDeviceAssignedToUser) ? newDeviceAssignedToUser : undefined;
    
    const payload = {
      deviceId: newDeviceIMEI.trim(),
      model: newDeviceModel.trim(),
      name: newDeviceName.trim(),
      imei: newDeviceIMEI.trim(),
      msdn: newDeviceMSDN?.trim() || "",  // 🎯 FIX: Empty string instead of undefined
      imsi: newDeviceIMSI?.trim() || "",  // 🎯 FIX: Empty string instead of undefined
      assignedToUser: validUser,
      deviceType: newDeviceType || "hardwire",
      vehicleType: newDeviceVehicleType || "car"
    };

    console.log("📤 Creating Device with payload:", payload); // 🎯 Debug log

    const response = await addDevice(payload).unwrap();

    if (response) {
      let msg = response.msg || "Device registered successfully.";
      if (response.creditsRemaining !== undefined) {
        msg += ` Remaining license count: ${response.creditsRemaining}.`;
      }
      
      showStatus('success', msg);

      // Reset all fields
      setNewDeviceModel(""); 
      setNewDeviceName(""); 
      setNewDeviceIMEI("");
      setNewDeviceMSDN("");
      setNewDeviceIMSI("");
      setNewDeviceVehicleType("car");
      setNewDeviceType("hardwire");
      setIsIMEIValid(false); 
      
      if (setShowAddDeviceModal) setShowAddDeviceModal(false);
      if (fetchAdminData) fetchAdminData();
    }

  } catch (err) {
    console.error("❌ Add Device Error:", err);
    
    // 🎯 Detailed error handling - show actual backend message
    const status = err?.status;
    const errorData = err?.data;
    
    // 🎯 FIX: First try to get actual message from backend
    let errorMsg = errorData?.message || errorData?.msg || errorData?.error;
    
    // Only use generic messages if backend didn't send one
    if (!errorMsg) {
      if (status === 400) {
        errorMsg = "Invalid device data. Check all fields.";
      } else if (status === 401) {
        errorMsg = "Session expired. Please login again.";
      } else if (status === 403) {
        errorMsg = "You don't have permission to add devices.";
      } else if (status === 409) {
        errorMsg = "Device with this IMEI already exists.";
      } else if (status === 402) {
        errorMsg = "No license count is available for this device registration.";
      } else if (status === 404) {
        errorMsg = "IMEI not found in assets. Add it as asset first.";
      } else if (status === 500) {
        errorMsg = "Server error. Please try again later.";
      } else {
        errorMsg = "Device could not be registered.";
      }
    }
    
    showStatus('error', getReadableStatusMessage(errorMsg, "error"));
  }
}, [
  newDeviceModel, newDeviceName, newDeviceIMEI, newDeviceMSDN, newDeviceIMSI, 
  newDeviceAssignedToUser, newDeviceType, newDeviceVehicleType, addDevice, 
  showStatus, setShowAddDeviceModal, fetchAdminData, setNewDeviceVehicleType,
  setNewDeviceModel, setNewDeviceName, setNewDeviceIMEI, setNewDeviceMSDN, 
  setNewDeviceIMSI, setNewDeviceType, setIsIMEIValid  // 🎯 FIX: Added missing dependencies
]);


const handleEditDevice = useCallback(async (eventOrPayload) => {
  if (eventOrPayload?.preventDefault) eventOrPayload.preventDefault();

  const source =
    eventOrPayload && !eventOrPayload.preventDefault
      ? eventOrPayload
      : editingDevice;

  if (!source?._id) {
    showStatus('error', "Device details could not be loaded for editing.");
    return;
  }

  // ✅ Remove non-editable or problematic fields
  const {
    harshAcceleration,
    harshBraking,
    liveData,
    eventHistory,
    locationHistory,
    status,
    vehicleStatus,
    runtimeStatus,
    runtimeStatusUpdatedAt,
    runtimeLastPacketAt,
    runtimeHasEverReported,
    runtimeLicenseExpired,
    runtimeAvailabilityStatus,
    availabilityStatus,
    movementStatus,
    currentLocation,
    licenseRuntime,
    ...cleanDevice
  } = source;

  try {
    await editDevice({ deviceId: cleanDevice._id, ...cleanDevice }).unwrap();
    if (typeof fetchAdminData === "function") {
      await fetchAdminData();
    }
    setShowEditDeviceModal(false);
    showStatus('success', "Device updated successfully.");
  } catch (err) {
    showStatus('error', getReadableStatusMessage(err, "error"));
  }
}, [editingDevice, editDevice, fetchAdminData, setShowEditDeviceModal, showStatus]);

const handleGeofenceManagement = useCallback((device) => {
  console.log("🗺️ Opening geofence modal for device:", device.name);
  setSelectedDeviceForGeofence(device);
  setUnsavedGeofence(null);
  setGeofenceName("");
  setShowGeofenceModal(true);
}, [setSelectedDeviceForGeofence, setUnsavedGeofence, setGeofenceName, setShowGeofenceModal]);

const onGeofenceCreated = useCallback((geofenceData) => {
  console.log("✅ Geofence created in handler:", geofenceData);
  setUnsavedGeofence(geofenceData);
}, [setUnsavedGeofence]);

const onGeofenceEdited = useCallback((geofenceData) => {
  console.log("✏️ Geofence edited in handler:", geofenceData);
  setUnsavedGeofence(geofenceData);
}, [setUnsavedGeofence]);

const onGeofenceDeleted = useCallback(() => {
  console.log("🗑️ Geofence deleted");
  setUnsavedGeofence(null);
}, [setUnsavedGeofence]);

const handleSaveGeofence = useCallback(async () => {
  if (!selectedDeviceForGeofence || !unsavedGeofence || !geofenceName) {
    console.error("❌ Cannot save: Missing required data", {
      hasDevice: !!selectedDeviceForGeofence,
      hasGeofence: !!unsavedGeofence,
      hasName: !!geofenceName
    });
    showStatus('warning', "Please complete all required fields before saving the geofence.");
    return;
  }

  console.log("💾 Saving geofence:", {
    device: selectedDeviceForGeofence.name,
    type: unsavedGeofence.type,
    name: geofenceName
  });

  try {
    let payload;
    
    if (unsavedGeofence.type === "circle") {
      payload = {
        name: geofenceName.trim(),
        type: "circle",
        center: {
          lat: Number(unsavedGeofence.data.center.lat || unsavedGeofence.data.center[0]),
          lng: Number(unsavedGeofence.data.center.lng || unsavedGeofence.data.center[1])
        },
        radius: Number(unsavedGeofence.data.radius),
        isActive: true
      };
      console.log("⭕ Circle payload:", payload);
    } else if (unsavedGeofence.type === "polygon") {
      const coords = unsavedGeofence.data.map(p => [
        Number(p.lng), 
        Number(p.lat)
      ]);
      
      // Ensure polygon is closed
      if (coords[0][0] !== coords[coords.length - 1][0] || 
          coords[0][1] !== coords[coords.length - 1][1]) {
        coords.push(coords[0]);
      }
      
      payload = {
        name: geofenceName.trim(),
        type: "polygon",
        polygon: {
          type: "Polygon",
          coordinates: [coords]
        },
        isActive: true
      };
      console.log("🔷 Polygon payload:", payload);
    }

    console.log("📤 Sending geofence to backend...");
    await editDevice({
      deviceId: selectedDeviceForGeofence._id,
      geofences: [payload]
    }).unwrap();

    console.log("✅ Geofence saved successfully");
    
    if (fetchAdminData) {
      await fetchAdminData();
    }
    
    setShowGeofenceModal(false);
    setUnsavedGeofence(null);
    setGeofenceName("");
    
    showStatus("success", "Geofence saved successfully.");
  } catch (err) {
    console.error("❌ Save geofence error:", err);
    showStatus("error", getReadableStatusMessage(err, "error"));
  }
}, [
  selectedDeviceForGeofence,
  unsavedGeofence,
  geofenceName,
  editDevice,
  fetchAdminData,
  showStatus,
  setShowGeofenceModal,
  setUnsavedGeofence,
  setGeofenceName
]);


const handleDeleteGeofence = useCallback(async () => {
  if (!selectedDeviceForGeofence?._id) {
    showStatus("warning", "Please select a device first.");
    return;
  }

  if (!window.confirm("Delete this geofence permanently?")) return;

  try {
    await editDevice({
      deviceId: selectedDeviceForGeofence._id,
      geofences: [] // ✅ THIS deletes saved geofence in DB
    }).unwrap();

    // ✅ UI cleanup
    setUnsavedGeofence(null);
    setGeofenceName("");
    setShowGeofenceModal(false);

    if (fetchAdminData) await fetchAdminData();

    showStatus("success", "Geofence deleted successfully.");
  } catch (err) {
    console.error("❌ Delete geofence error:", err);
    showStatus("error", getReadableStatusMessage(err, "error"));
  }
}, [
  selectedDeviceForGeofence,
  editDevice,
  fetchAdminData,
  showStatus,
  setUnsavedGeofence,
  setGeofenceName,
  setShowGeofenceModal
]);

  // ✅ RETURN FIX: Exporting NEW DEVICE setters correctly
  return {
     vehicleTypeIcons, newDeviceVehicleType,       // 👈 ADD THIS
  setNewDeviceVehicleType,    // 👈 ADD THIS
  vehicleTypeIcons,
  handleDeleteGeofence,
    handleOpenCommandModal, handleCloseCommandModal, handleIMEIValidation, handleCancelValidation: () => setIsIMEIValid(false),
    handleResetPassword, handleEditUser, handleDeleteUser, handleRestoreUser, handleEditDevice, 
    handleDeleteDevice: async (deviceOrId) => {
      const deviceId =
        typeof deviceOrId === "object"
          ? deviceOrId?._id || deviceOrId?.id
          : deviceOrId;
      if (!deviceId) return;

      const isDeletedDevice =
        typeof deviceOrId === "object" &&
        String(deviceOrId?.status || "").toLowerCase() === "deleted";

      const confirmationMessage = isDeletedDevice
        ? "This vehicle is already soft deleted. Delete again to permanently remove it from the database?"
        : "This vehicle will be removed from the fleet list for users now, but it will stay in the database until you delete it one more time. Continue?";

      if (!window.confirm(confirmationMessage)) return;
      try {
        const response = await deleteDevice(deviceId).unwrap();
        if (fetchAdminData) await fetchAdminData();
        showStatus(
          'success',
          response?.msg || (isDeletedDevice ? "Device permanently deleted successfully." : "Device soft deleted successfully.")
        );
      } catch (err) {
        showStatus('error', getReadableStatusMessage(err, "error"));
      }
    }, 
    handleGeofenceManagement, onGeofenceCreated: (e) => setUnsavedGeofence(e), onGeofenceEdited: (e) => setUnsavedGeofence(e),
    onGeofenceDeleted: () => setUnsavedGeofence(null), handleSaveGeofence, 
    getDirections: (lat, lng) => { setRouteEnd([lat, lng]); },
    formatTimestamp12h, handleCreateUser, getVehicleIcon,
    
    // States & Setters
    newDeviceModel, setNewDeviceModel, 
    newDeviceName, setNewDeviceName, 
    newDeviceIMEI, setNewDeviceIMEI,
    newDeviceMSDN, setNewDeviceMSDN, 
    newDeviceIMSI, setNewDeviceIMSI, 
    newDeviceAssignedToUser, setNewDeviceAssignedToUser,
    newDeviceType, setNewDeviceType, // 👈 FIXED: ADDED THIS HERE
    
    newUsername, setNewUsername, newEmail, setNewEmail, newMobile, setNewMobile, newPassword, setNewPassword, newUserRole, setNewUserRole,
    bulkFile, uploading, showCommandModal, deviceForCommand, commandModalOptions, isIMEIValid,
    handleAssignDevice: async (e) => {
      e.preventDefault();
      try {
        await editDevice({ deviceId: assignDeviceId, assignedToUser: assignToUserId }).unwrap();
        if (fetchAdminData) await fetchAdminData();
        showStatus('success', "Device assignment updated successfully.");
      } catch (err) {
        showStatus('error', getReadableStatusMessage(err, "error"));
      }
    },
    handleAssignUserToGroupAdmin: async (e) => {
      e.preventDefault();
      try {
        await assignUserToGroupAdmin({ groupAdminId: assignUserToGroupAdminTargetId, userIdToManage: assignUserToGroupAdminUserId }).unwrap();
        if (fetchAdminData) await fetchAdminData();
        showStatus('success', "User assignment updated successfully.");
      } catch (err) {
        showStatus('error', getReadableStatusMessage(err, "error"));
      }
    },
    handleRemoveManagedUser: async (gId, uId) => {
      if (!window.confirm("Remove?")) return;
      try {
        await removeManagedUser({ groupAdminId: gId, userIdToRemove: uId }).unwrap();
        if (fetchAdminData) await fetchAdminData();
        showStatus('info', "Managed user removed successfully.");
      } catch (err) {
        showStatus('error', getReadableStatusMessage(err, "error"));
      }
    },
    handleAddDevice, 
handleAddAsset: async (e) => { 
  e.preventDefault(); 

  const normalizedModel =
    newAssetData.model === "Other"
      ? String(newAssetData.customModel || "").trim()
      : String(newAssetData.model || "").trim();
  
  if (!newAssetData.IMEI || newAssetData.IMEI.length !== 15) {
    showStatus('error', "Enter a valid 15-digit IMEI number.");
    return;
  }
  if (!normalizedModel) {
    showStatus('error', "Select a device model before continuing.");
    return;
  }
  
  try { 
    const payload = {
      ...newAssetData,
      model: normalizedModel,
      serialNumber: String(newAssetData.serialNumber || "").trim(),
      notes: String(newAssetData.notes || "").trim(),
      simCard: {
        IMSI: String(newAssetData.simCard?.IMSI || "").trim(),
        MSDN: String(newAssetData.simCard?.MSDN || "").trim(),
        provider: String(newAssetData.simCard?.provider || "").trim(),
        expiryDate: newAssetData.simCard?.expiryDate || "",
      },
    };

    const response = await addAsset(payload).unwrap(); 
    showStatus('success', response?.msg || response?.message || "Asset added successfully.");
    setShowAddAssetModal(false);
    setNewAssetData({
      IMEI: "", serialNumber: "", model: "", type: "hardwire", status: "unassigned",
      customModel: "",
      simCard: { IMSI: "", MSDN: "", provider: "", expiryDate: "" },
      purchaseDate: "", notes: "",
    });
    if (fetchAdminData) fetchAdminData();
  } catch (err) { 
    console.error("Add Asset Error:", err);
    // 🎯 FIX: Show actual backend message
    showStatus('error', getReadableStatusMessage(err, "error"));
  } 
},
    handleEditAsset: async (e) => {
      e.preventDefault();
      try {
        await editAsset({ assetId: editingAsset._id, ...editingAsset }).unwrap();
        if (fetchAdminData) await fetchAdminData();
        setEditingAsset(null);
        setShowEditAssetModal(false);
        showStatus('success', "Asset updated successfully.");
      } catch (err) {
        showStatus('error', getReadableStatusMessage(err, "error"));
      }
    },
    handleDeleteAsset: async (id) => {
      try {
        await updateAssetStatus({ assetId: id, status: "deleted" }).unwrap();
        if (fetchAdminData) await fetchAdminData();
        showStatus('info', "Asset deleted successfully.");
      } catch (err) {
        showStatus('error', getReadableStatusMessage(err, "error"));
      }
    },
    handlePermanentDeleteAsset: async (id) => {
      if (!window.confirm("Permanent?")) return;
      try {
        await permanentDeleteAsset(id).unwrap();
        if (fetchAdminData) await fetchAdminData();
        showStatus('success', "Asset permanently deleted.");
      } catch (err) {
        showStatus('error', getReadableStatusMessage(err, "error"));
      }
    },
    handleRestoreAsset: async (id) => {
      try {
        await updateAssetStatus({ assetId: id, status: "unassigned" }).unwrap();
        if (fetchAdminData) await fetchAdminData();
        showStatus('success', "Asset restored successfully.");
      } catch (err) {
        showStatus('error', getReadableStatusMessage(err, "error"));
      }
    },
    handleDownloadTemplate: async () => {
      try {
        await downloadEntityTemplate("assets");
      } catch (err) {
        showStatus("error", getReadableStatusMessage(err?.response?.data?.message || "Template could not be downloaded.", "error"));
      }
    },
    handleBulkUpload: async () => {
      if (!bulkFile) return;
      setUploading(true);
      try {
        const result = await uploadEntityFile("assets", bulkFile, { validateOnly: false });
        showStatus("success", result?.message || "Assets uploaded successfully.");
        setBulkFile(null);
      } catch (err) {
        const errors = err?.response?.data?.errors;
        const msg = Array.isArray(errors) && errors.length
          ? `${err?.response?.data?.message || "Upload failed"}: ${errors.slice(0, 3).join(" | ")}`
          : err?.response?.data?.message || "Upload failed.";
        showStatus("error", getReadableStatusMessage(msg, "error"));
      } finally {
        setUploading(false);
      }
    },
    handleBulkFileChange: (e) => setBulkFile(e.target.files[0]),
  };
}
